<template>
    <div>
        <div class="d-flex flex-wrap justify-content-end align-items-start">
            <!-- Page View Only - Custom Logo Display -->
            <div
                v-if="isPageViewerSession && customLogoIsDisplayed && !loading"
                class="flex-grow-0 d-flex align-items-center navbar-nav-tools mr-1"
            >
                <li class="nav-item pl-0 pl-md-2 mt-0">
                    <div class="flex-grow-0 logo-sm d-flex align-items-center rounded-circle custom-logo-circle page-logo-circle">
                        <img :src="brandLogoPath" alt="Creator logo"/>
                    </div>
                </li>
            </div>
            <!-- Author Toolbar -->
            <ul
                class="d-none d-md-flex bg-toolbar rounded-lg align-items-center navbar-nav-tools session-navbar"
                v-if="showAuthorTools"
            >
                <li class="nav-item">
                    <div class="nav-link py-1 pr-0">
                        <info-popover content="View all comments" placement="bottom">
                            <button
                                @click="openComments()"
                                class="btn btn-transparent nowrap"
                                aria-label="View all comments"
                            >
                                <span class="icon icon-xl icon-glyphicons-basic-comments align-middle"></span>
                                <span v-show="unresolvedCommentCount > 0" class="badge badge-primary comments-badge">
                                    {{ unresolvedCommentCount }}
                                </span>
                                <span
                                    v-show="allCommentsResolved"
                                    class="icon icon-glyphicons-basic-check text-success align-middle"
                                    style="font-size: 80%"
                                ></span>
                            </button>
                        </info-popover>
                    </div>
                </li>
                <li v-show="editMode && showAuthorTools" class="nav-item">
                    <span class="nav-link py-1 pr-0">
                        <div class="dropdown">
                            <button
                                data-toggle="dropdown"
                                class="btn btn-transparent"
                                aria-label="View notes"
                                type="button"
                                data-offset="0,7"
                            >
                                <span class="icon icon-xl icon-glyphicons-basic-palette align-middle"></span>
                            </button>
                            <div class="dropdown-menu global-style-dropdown shadow-sm p-3" id="theme-drop">
                                <global-style-form :value="sessionCase" :auto-save="true"></global-style-form>
                            </div>
                        </div>
                    </span>
                </li>
                <li v-show="editMode && showAuthorTools" class="nav-item">
                    <span class="nav-link py-1 pl-2 pr-1">
                        <info-popover content="Reset preview session" placement="bottom">
                            <button class="btn btn-transparent" @click="openResetSession()">
                                <span class="icon icon-xl icon-glyphicons-basic-refresh align-middle"></span>
                            </button>
                        </info-popover>
                    </span>
                </li>
                <li v-if="showAuthorTools" class="nav-item d-flex align-items-center">
                    <div class="nav-link d-flex align-items-center ml-1">
                        <div class="custom-control custom-switch edit-switch mb-0 icon-mr">
                            <input
                                v-model="editMode"
                                type="checkbox"
                                class="custom-control-input"
                                id="editModeSwitch"
                            />
                            <label class="custom-control-label" for="editModeSwitch"></label>
                        </div>
                        <div class="edit-switch-label">
                            Edit Mode:
                            <span v-show="editMode" class="text-primary">On</span>
                            <span v-show="!editMode">Off</span>
                        </div>
                    </div>
                </li>
            </ul>
            <!-- Timer Toolbar -->
            <ul
                class="d-flex nav-scroll-x bg-toolbar rounded-lg align-items-center navbar-nav-tools session-navbar learner-toolbar px-sm-1 global-theme"
                v-if="hasTimers"
            >
                <li class="nav-item">
                    <span class="nav-link">
                        <node-timer
                            v-for="timer in sessionTimers"
                            :key="timer.id"
                            :timer="timer"
                            v-on:timer-expired="timerExpired($event)"
                        ></node-timer>
                    </span>
                </li>
            </ul>
            <!-- Learner Toolbar -->
            <ul
                class="d-flex nav-scroll-x bg-toolbar rounded-lg align-items-center navbar-nav-tools learner-toolbar session-navbar"
            >
                <div class="d-flex mr-1 global-theme">
                    <li class="nav-item" v-if="caseObj.learner_resources_enabled">
                        <div class="nav-link py-1 pr-0 d-flex align-items-center">
                            <info-popover :content="labelsTranslated.openLearnerResources" placement="bottom">
                                <button
                                    @click="openResources()"
                                    class="btn btn-transparent nowrap"
                                    aria-label="View learner resources"
                                    data-v-tour="resources"
                                >
                                    <span class="icon icon-xl icon-glyphicons-basic-book-open align-middle"></span>
                                    <unread-marker class="unread-marker" v-if="!hasOpenedResources" />
                                </button>
                            </info-popover>
                        </div>
                    </li>
                    <li class="nav-item" v-if="caseObj.global_notepad_enabled">
                        <span class="nav-link py-1 pr-0 d-flex align-items-center">
                            <info-popover :content="labelsTranslated.openNotepad" placement="bottom">
                                <button
                                    @click="openNotes()"
                                    class="btn btn-transparent"
                                    aria-label="View notes"
                                    data-v-tour="notes"
                                >
                                    <span class="icon icon-xl icon-glyphicons-basic-notes align-middle"></span>
                                </button>
                            </info-popover>
                        </span>
                    </li>
                    <li class="nav-item" v-if="showCommentsBtn">
                        <span class="nav-link py-1 pr-0 d-flex align-items-center">
                            <info-popover content="View Comments" placement="bottom">
                                <button
                                    @click="openComments()"
                                    class="btn btn-transparent nowrap"
                                    aria-label="View comments"
                                    data-v-tour="comments"
                                >
                                    <span class="icon icon-xl icon-glyphicons-basic-comments align-middle"></span>
                                    <span v-show="comments.length > 0" class="badge badge-primary comments-badge">
                                        {{ comments.length }}
                                    </span>
                                </button>
                            </info-popover>
                        </span>
                    </li>
                </div>
                <li class="nav-item d-none d-lg-flex align-items-center ml-1" v-if="showLiveScore">
                    <span class="nav-link rounded py-1" data-v-tour="scorebar">
                        <session-score-bar
                            :score="currentSession.score"
                            :score-max="currentSession.score_max"
                        ></session-score-bar>
                    </span>
                </li>
                <li class="nav-item global-theme" v-if="isCommunityPublished && !showAuthorTools">
                    <a
                        :href="detailViewUrl"
                        target="_blank"
                        aria-label="Learn more about this resource"
                        class="nav-link community-detail-link"
                    >
                        <span class="align-middle">About</span>
                        <span class="icon-glyphicons-basic-square-new-window ml-1 align-middle"></span>
                    </a>
                </li>
                <li v-if="contentIncludesCreativeCommons" class="nav-item">
                    <div class="nav-link">
                        <creative-commons-icon></creative-commons-icon>
                    </div>
                </li>
                <li class="nav-item" v-if="showAuthorTools">
                    <span class="nav-link py-1 pr-0 d-flex align-items-center">
                        <info-popover :content="labelsTranslated.requestSupport" placement="bottom">
                            <a
                                class="btn btn-transparent"
                                aria-label="Open support request form"
                                @click="openSupportModal()"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-circle-question align-middle"></span>
                            </a>
                        </info-popover>
                    </span>
                </li>
                <li class="nav-item" v-if="showAuthorTools">
                    <span class="nav-link py-1 d-flex align-items-center">
                        <info-popover :content="labelsTranslated.submitFeedback" placement="bottom">
                            <a
                                class="btn btn-transparent"
                                data-toggle="modal"
                                data-target="#feedback-modal"
                                aria-label="Open feedback form"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-announcement align-middle"></span>
                            </a>
                        </info-popover>
                    </span>
                </li>
                <li class="nav-item nav-item-sm-hidden">
                    <span class="nav-link py-1 pl-0 d-flex align-items-center">
                        <info-popover :content="fullScreenBtnLabel" placement="bottom">
                            <button
                                class="btn btn-transparent"
                                aria-label="Enter full screen"
                                @click="toggleFullScreen()"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-fullscreen align-middle"></span>
                            </button>
                        </info-popover>
                    </span>
                </li>
            </ul>
        </div>

        <div class="d-flex d-lg-none justify-content-end mt-3" data-v-tour="scorebar-sm" v-if="showLiveScore">
            <session-score-bar :score="currentSession.score" :score-max="currentSession.score_max"></session-score-bar>
        </div>

        <!-- Creative Commons Attribution -->
        <div v-if="isCreativeCommonsDerivative" class="d-flex justify-content-end cc-derivation-container">
            <div v-for="caseObj in derivedFromCases" :key="caseObj.id" class="cc-derivation-item">
                <span class="global-theme">
                    <span class="nav-link community-detail-link mx-1 py-0 tiny text-secondary cc-derivation">
                        <span class="align-middle max-two-lines"
                            >This content is adapted from
                            <a
                                :href="communityBrowseUrl(caseObj.id)"
                                target="_blank"
                                aria-label="Learn more about this resource"
                                >{{ trimString(caseObj.community_title, 30) }} by
                                {{ contributorListDisplay(caseObj) }}
                                <span class="icon-glyphicons-basic-square-new-window ml-1 align-middle"></span>
                            </a>
                        </span>
                    </span>
                </span>
            </div>
        </div>

        <div>
            <!-- Comments Modal -->
            <base-modal id="comment-modal" :loading="modalLoading" size="medium" :centered="true">
                <template slot="title">
                    <span class="align-middle icon icon-glyphicons-basic-comments mr-2"></span>
                    <span class="align-middle">Reviewer Comments</span>
                </template>
                <template>
                    <p v-show="comments.length == 0" class="text-secondary text-center mt-3">
                        No one has commented on this screen yet.
                    </p>
                    <div class="my-2">
                        <node-comments
                            :comments="comments"
                            :edit-mode="true"
                            v-on:open-edit-username="openUsernameModal()"
                            v-on:open-edit-comment="openEditComment($event)"
                            v-on:open-remove-comment="openRemoveComment($event)"
                        ></node-comments>
                    </div>
                </template>
                <template slot="footer">
                    <div class="w-100">
                        <textarea
                            v-model="currentComment"
                            rows="3"
                            maxlength="2000"
                            class="form-control mb-1"
                            placeholder="Type a comment about this screen here..."
                            aria-label="Your comment about this screen"
                        ></textarea>
                    </div>
                    <div class="d-flex justify-content-end w-100">
                        <button class="btn btn-secondary btn-sm mr-2" type="button" data-dismiss="modal">Close</button>
                        <button
                            class="btn btn-primary btn-sm"
                            @click="postComment()"
                            :disabled="modalLoading || this.currentComment == ''"
                            type="button"
                        >
                            Comment
                        </button>
                    </div>
                </template>
            </base-modal>

            <!-- Edit Comment Modal -->
            <base-modal id="edit-comment-modal" :loading="modalLoading" size="medium" :centered="true">
                <template slot="title">Edit Comment</template>
                <template>
                    <textarea
                        v-model="selectedComment.content"
                        rows="3"
                        maxlength="2000"
                        class="form-control"
                        placeholder="Type a comment about this screen here..."
                    ></textarea>
                </template>
                <template slot="footer">
                    <button @click="closeEditComment()" class="btn btn-sm btn-secondary" type="button">Cancel</button>
                    <button
                        @click="updateComment()"
                        :disabled="modalLoading || !selectedComment.content"
                        class="btn btn-sm btn-primary"
                        type="button"
                    >
                        Update
                    </button>
                </template>
            </base-modal>

            <!-- Remove Comment Modal -->
            <base-modal id="remove-comment-modal" :loading="modalLoading" size="medium" :centered="true">
                <template slot="title">Remove Comment?</template>
                <template>
                    <div>
                        <div class="mb-2">Your comment:</div>
                        <textarea
                            v-model="selectedComment.content"
                            rows="3"
                            maxlength="2000"
                            class="form-control"
                            disabled
                        ></textarea>
                    </div>
                </template>
                <template slot="footer">
                    <button @click="closeEditComment()" class="btn btn-sm btn-secondary" type="button">Cancel</button>
                    <button
                        @click="removeComment()"
                        :disabled="modalLoading"
                        class="btn btn-sm btn-danger"
                        type="button"
                    >
                        Remove
                    </button>
                </template>
            </base-modal>

            <!-- Review Comments Modal -->
            <base-modal id="review-comments-modal" :loading="modalLoading" :centered="true">
                <template slot="title">
                    <span class="align-middle icon icon-glyphicons-basic-chat-conversation mr-2"></span>
                    <span class="align-middle">Comments</span>
                </template>
                <template>
                    <node-comments
                        :comments="comments"
                        :edit-mode="false"
                        :show-admin-tools="true"
                        :show-link-details="true"
                        class="mt-2"
                    ></node-comments>
                    <div v-show="comments.length === 0 && !modalLoading" class="text-secondary text-center my-3">
                        No comments have been left on this screen yet.
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-light" type="button" data-dismiss="modal">Done</button>
                </template>
            </base-modal>

            <!-- Support Modal -->
            <base-modal id="session-support-modal" :centered="true" class="global-theme">
                <template slot="title">
                    <span class="icon icon-100 icon-glyphicons-basic-circle-question align-middle icon-mr"></span>
                    <span class="align-middle">Request Support</span>
                </template>
                <template>
                    <p>Send us a message and someone on our team will get back to you shortly.</p>
                    <base-form v-model="support" :fields="supportFormFields" ref="supportForm"></base-form>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button
                        @click="sendSupport()"
                        :disabled="modalLoading || support.message === ''"
                        class="btn btn-primary"
                        type="button"
                    >
                        Send
                    </button>
                </template>
            </base-modal>

            <!-- Username Modal -->
            <base-modal
                id="session-name-modal"
                :loading="modalLoading"
                size="medium"
                :centered="true"
                class="global-theme"
            >
                <template slot="title">Set your commenter name</template>
                <template>
                    <p class="mb-4">
                        Provide a name or alias to identify your comments to other reviewers. The name you choose will
                        be visible to the other users accessing this link.
                    </p>
                    <base-form
                        v-model="anonymousUser"
                        :fields="anonymousUserFields"
                        :inline-style="true"
                        ref="anonymousUserForm"
                    ></base-form>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button @click="saveUsername()" :disabled="modalLoading" class="btn btn-primary" type="button">
                        Done
                    </button>
                </template>
            </base-modal>

            <!-- Notes Modal -->
            <base-modal id="notepad-modal" :loading="modalLoading" size="medium" :centered="true" class="global-theme">
                <template slot="title">
                    <span class="icon icon-glyphicons-basic-notes align-middle mr-1"></span>
                    <span class="align-middle"> {{ labelsTranslated.notepad }} </span>
                </template>
                <template>
                    <textarea
                        @input="saveNotes()"
                        v-model="learnerNotes"
                        maxlength="5000"
                        class="form-control mh-100"
                        rows="18"
                        placeholder="Enter your notes here..."
                    ></textarea>
                    <div class="mt-2 d-flex align-items-center">
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center" style="height: 24px">
                                <div v-show="notesSaving">
                                    <div class="d-flex align-items-center">
                                        <div
                                            class="spinner-border spinner-border-sm text-primary ml-1 mr-2"
                                            role="status"
                                        >
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                        <div class="text-secondary small font-weight-500">
                                            {{ labelsTranslated.notesSaving }}...
                                        </div>
                                    </div>
                                </div>
                                <div v-show="!notesSaving">
                                    <span class="icon icon-glyphicons-basic-circle-check ml-1 mr-1 text-success"></span>
                                    <span class="text-secondary small font-weight-500">{{
                                        labelsTranslated.notesSaved
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow-0">
                            <button class="btn btn-gray btn-sm" type="button" data-dismiss="modal">Close</button>
                        </div>
                    </div>
                </template>
            </base-modal>

            <!-- Resources Modal -->
            <base-modal
                id="resources-modal"
                :loading="resourceModalLoading"
                size="large"
                :centered="true"
                class="global-theme"
            >
                <template>
                    <div class="d-flex mb-2 pb-2">
                        <div class="flex-grow-1">
                            <h2 class="h5">
                                <span class="icon icon-glyphicons-basic-book-open align-middle mr-2"></span>
                                <span class="align-middle">{{ labelsTranslated.resources }}</span>
                            </h2>
                        </div>
                        <div class="flex-grow-0">
                            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                    </div>
                    <div>
                        <ul class="nav nav-tabs">
                            <div class="nav-item" v-for="tab in learnerResourceTabs" :key="tab.id">
                                <a
                                    class="nav-link"
                                    :class="{ active: tab.id == selectedResourceTab.id }"
                                    @click="selectResourceTab(tab)"
                                >
                                    {{ tab.label }}
                                </a>
                            </div>
                        </ul>
                        <div
                            class="pt-3"
                            ref="resourceTabContainer"
                            :style="{ 'min-height': minResourceTabHeight + 'px' }"
                        >
                            <rich-content-display
                                :elements="selectedResourceTab.elements"
                                :editable="editMode && showAuthorTools"
                            ></rich-content-display>
                        </div>
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-gray" type="button" data-dismiss="modal">Close</button>
                </template>
            </base-modal>

            <!-- Reset Session Modal -->
            <base-modal id="reset-session-modal" :loading="modalLoading" size="medium" :centered="true">
                <template slot="title">
                    <span class="align-middle">Reset Preview Session</span>
                </template>
                <template> Reset the state of your current preview session? </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button @click="resetSession()" :disabled="modalLoading" class="btn btn-primary" type="button">
                        <span class="icon icon-glyphicons-basic-refresh mr-1"></span>
                        <span>Reset</span>
                    </button>
                </template>
            </base-modal>
        </div>
        <!-- Toolbar Tour -->
        <v-tour
            name="sessionToolbarTour"
            :steps="toolbarTourSteps"
            :options="{ startTimeout: 300, highlight: true }"
            class="global-theme"
        >
            <template slot-scope="tour">
                <transition name="fade">
                    <v-step
                        v-if="tour.steps[tour.currentStep]"
                        :key="tour.currentStep"
                        :step="tour.steps[tour.currentStep]"
                        :previous-step="tour.previousStep"
                        :next-step="tour.nextStep"
                        :stop="tour.stop"
                        :skip="tour.skip"
                        :is-first="tour.isFirst"
                        :is-last="tour.isLast"
                        :labels="tour.labels"
                        :highlight="tour.highlight"
                    >
                        <template>
                            <div slot="header" class="d-flex align-content-start mb-2">
                                <div class="flex-grow-1 mr-2">
                                    <div class="tour-header mb-0">{{ tour.steps[tour.currentStep].header }}</div>
                                </div>
                                <div class="flex-grow-0">
                                    <button
                                        @click="tour.stop"
                                        type="button"
                                        class="btn btn-icon text-light p-0"
                                        aria-label="Close"
                                        style="margin-top: -6px"
                                    >
                                        <span class="icon icon-glyphicons-basic-menu-close"></span>
                                    </button>
                                </div>
                            </div>
                            <div slot="content" class="medium tour-content">
                                {{ tour.steps[tour.currentStep].content }}
                            </div>
                            <div slot="actions">
                                <div class="d-flex">
                                    <div class="flex-grow-1">
                                        <button
                                            @click="tour.stop"
                                            class="btn btn-sm btn-link text-light px-1"
                                            style="margin-left: -5px"
                                        >
                                            {{ labelsTranslated.skipTour }}
                                        </button>
                                    </div>
                                    <div class="flex-grow-0">
                                        <button @click="tour.nextStep" class="btn btn-sm btn-light" v-if="!tour.isLast">
                                            {{ labelsTranslated.next }}
                                        </button>
                                        <button @click="tour.stop" class="btn btn-light btn-sm" v-else>
                                            {{ labelsTranslated.gotIt }}
                                        </button>
                                    </div>
                                </div>
                                <div class="pt-2">
                                    <button
                                        @click="silenceAllTours(tour)"
                                        class="btn btn-link tour-btn-silence text-light px-1 py-0"
                                        style="margin-left: -5px"
                                    >
                                        <span class="icon icon-glyphicons-basic-bell-off mr-1 align-middle"></span>
                                        <span class="align-middle">{{ labelsTranslated.silenceTours }}</span>
                                    </button>
                                </div>
                            </div>
                        </template>
                    </v-step>
                </transition>
            </template>
        </v-tour>
    </div>
</template>

<script>
import http from "@/axios.js"
import debounce from "lodash.debounce"
import NodeTimer from "@/components/NodeTimer"
import SessionScoreBar from "@/components/SessionScoreBar"
import InfoPopover from "@/components/InfoPopover"
import Utilities from "@/assets/js/utilities.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import NodeComments from "@/components/NodeComments"
import RichContentDisplay from "@/components/RichContentDisplay"
import UnreadMarker from "@/components/UnreadMarker"
import GlobalStyleForm from "@/components/GlobalStyleForm"
import CreativeCommonsIcon from "@/components/CreativeCommonsIcon"

export default {
    name: "SessionToolbar",
    components: {
        NodeTimer,
        SessionScoreBar,
        InfoPopover,
        BaseModal,
        BaseForm,
        NodeComments,
        RichContentDisplay,
        UnreadMarker,
        GlobalStyleForm,
        CreativeCommonsIcon,
    },
    props: {
        caseObj: {
            type: Object,
            required: true,
        },
        showCommentsBtn: {
            type: Boolean,
            required: false,
            default: false,
        },
        showAuthorTools: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            editMode: this.$store.getters.previewEditMode,
            comments: [],
            currentComment: "",
            support: {
                email: null,
                message: "",
            },
            selectedComment: {
                content: "",
            },
            anonymousUser: {
                username: "",
            },
            anonymousUserFields: [
                {
                    name: "Name",
                    key: "username",
                    type: "text",
                    placeholder: "Commenter name (i.e. name, initials, alias)",
                    maxlength: 200,
                    required: true,
                },
            ],
            modalLoading: false,
            resourceModalLoading: false,
            notesSaving: false,
            learnerNotes: "",
            learnerResourceTabs: [],
            selectedResourceTab: {
                id: null,
                elements: [],
            },
            minResourceTabHeight: 160,
            hasOpenedResources: false,
            fullScreenActive: false,
            toolbarTourSteps: [],
            toolbarTourCallbacks: {
                onFinish: this.endToolbarTourCb,
            },
            hasViewedTour: false,
            derivedFromCases: [],
        }
    },
    watch: {
        editMode: function (newVal) {
            this.$store.commit("setPreviewEditMode", newVal)
        },
        currentNodeId: function () {
            // Fetch comments for the node if enabled.
            this.fetchComments()
            // Clear the current comment.
            this.currentComment = ""
            this.updateDerivativeInfo()
        },
        storedNotes: function (newVal) {
            this.learnerNotes = newVal
        },
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        sessionID() {
            return this.$route.params.sessionID
        },
        currentSession() {
            return this.$store.getters.session
        },
        sessionTimers() {
            return this.$store.getters.sessionTimers
        },
        sessionCase() {
            return this.$store.getters.sessionCase
        },
        currentNode() {
            return this.$store.getters.sessionNode
        },
        currentPanels() {
            return this.$store.getters.sessionCurrentPanels
        },
        userIsAnonymous() {
            return this.$store.getters.userIsAnonymous
        },
        currentNodeId() {
            if (!this.currentNode) {
                return null
            }
            return this.currentNode.id
        },
        hasTimers() {
            if (!this.sessionTimers) {
                return false
            }
            return this.sessionTimers.length > 0
        },
        showLiveScore() {
            return this.currentSession && this.sessionCase && this.sessionCase.show_live_score
        },
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        isCommunityPublished() {
            return this.sessionCase && this.sessionCase.community_published_status === "published"
        },
        shareLink() {
            return this.currentSession.share_link
        },
        shareLinkNavigationEnabled() {
            return this.shareLink && this.shareLink.enable_node_navigation
        },
        isShareLinkSession() {
            return Boolean(this.shareLink)
        },
        isPageViewerSession() {
            return Boolean(this.$route.params.pageID)
        },
        storedNotes() {
            return this.$store.getters.sessionNotes
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        detailViewUrl() {
            return this.$router.resolve({ name: "CommunityCaseDetail", params: { id: this.caseObj.id } }).href
        },
        brandLogoPath() {
            return this.$store.getters.customLogoUrl
        },
        customLogoIsDisplayed() {
            return this.brandLogoPath !== null
        },
        fullScreenBtnLabel() {
            return this.fullScreenActive ? this.labelsTranslated.exitFullscreen : this.labelsTranslated.enterFullscreen
        },
        // Returns true if the case or current screen contains creative commons content. If true,
        // the screen is considered a derivative (because only sharealike licenses are supported),
        // so show the creative commons license icon.
        contentIncludesCreativeCommons() {
            let caseIsCC = this.caseObj.content_license == "cc-by-sa-4-0"
            let containsCCPanel = this.currentPanels.find((p) => p.content_license == "cc-by-sa-4-0")
            return caseIsCC || containsCCPanel
        },
        // Returns the ID of the case that the current screen was cloned/derived from.
        // This could be the panel or case.
        derivedFromCaseIds() {
            let derivedFromCaseIds = []

            let clonedCaseId = this.caseObj.cloned_from_community_case_id
            let clonedPanelCaseIds = this.currentPanels
                .filter((p) => p.cloned_from_community_case_id)
                .map((p) => p.cloned_from_community_case_id)

            if (clonedCaseId) {
                derivedFromCaseIds.push(clonedCaseId)
            }
            if (clonedPanelCaseIds) {
                derivedFromCaseIds = derivedFromCaseIds.concat(clonedPanelCaseIds)
            }
            // Filter out duplicate IDs.
            return [...new Set(derivedFromCaseIds)]
        },
        // Returns true if the current screen contains creative commons content.
        isCreativeCommonsDerivative() {
            return Boolean(this.derivedFromCaseIds.length)
        },
        supportFormFields() {
            const fields = [
                {
                    name: "",
                    placeholder: "Describe the issue you're experiencing here...",
                    key: "message",
                    type: "textarea",
                    rows: 10,
                    required: true,
                },
            ]
            // Add the email field if the user is anonymous.
            if (this.userIsAnonymous) {
                fields.unshift({
                    name: "Your Email",
                    placeholder: "Email",
                    key: "email",
                    type: "email",
                    required: true,
                })
            }
            return fields
        },
        unresolvedCommentCount() {
            return this.comments.filter((c) => !c.is_resolved).length
        },
        allCommentsResolved() {
            return this.comments.length > 0 && this.unresolvedCommentCount === 0
        },
    },
    methods: {
        // Emit a timer expired event up to the parent to be handled in the session component.
        timerExpired(timerId) {
            this.$emit("timer-expired", timerId)
        },
        // Emit the open-create event to be handled in the parent.
        openCreatePanel() {
            this.$emit("open-create-panel")
        },
        // Launch full screen mode.
        toggleFullScreen() {
            let element = document.documentElement
            let vm = this
            if (vm.fullScreenActive) {
                Utilities.exitFullScreen(element)
                vm.fullScreenActive = false
            } else {
                Utilities.enterFullScreen(element)
                vm.fullScreenActive = true
            }
        },
        // Fetch comments for the screen.
        fetchComments() {
            if (this.showAuthorTools && this.currentNodeId) {
                // Fetch all comments as an author.
                this.modalLoading = true
                let vm = this
                http.get(`/api/nodes/${this.currentNodeId}/comments/`)
                    .then((response) => {
                        vm.comments = response.data
                    })
                    .finally(() => {
                        vm.modalLoading = false
                    })
            } else if (this.showCommentsBtn && this.currentNodeId && this.currentSession.id) {
                // Fetch comments as a viewer.
                this.modalLoading = true
                let vm = this
                http.get(`/api/sessions/${this.currentSession.id}/nodes/${this.currentNodeId}/comments/`)
                    .then((response) => {
                        vm.comments = response.data
                    })
                    .finally(() => {
                        vm.modalLoading = false
                    })
            }
        },
        // Open the comments modal.
        openComments() {
            if (this.showAuthorTools) {
                // Open review comments modal to see all comments as an author.
                $("#review-comments-modal").modal("show")
            } else if (!this.currentSession.anonymous_username) {
                // If the user hasn't set their session name yet, open the name config modal.
                this.openUsernameModal()
            } else {
                $("#comment-modal").modal("show")
            }
        },
        // Open the edit comment modal.
        openEditComment(comment) {
            this.selectedComment = comment
            $("#comment-modal").modal("hide")
            $("#edit-comment-modal").modal("show")
        },
        // Open the remove comment modal.
        openRemoveComment(comment) {
            this.selectedComment = comment
            $("#comment-modal").modal("hide")
            $("#remove-comment-modal").modal("show")
        },
        // Close the edit comment modal (return to the main comment modal).
        closeEditComment() {
            $("#edit-comment-modal").modal("hide")
            $("#remove-comment-modal").modal("hide")
            $("#comment-modal").modal("show")
        },
        // Update a comment.
        updateComment() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/comments/${this.selectedComment.id}/`, {
                content: this.selectedComment.content,
            })
                .then(() => {
                    vm.fetchComments()
                    vm.selectedComment = {
                        content: "",
                    }
                    vm.closeEditComment()
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        // Remove a comment.
        removeComment() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/comments/${this.selectedComment.id}/remove/`)
                .then(() => {
                    let commentIndex = vm.comments.findIndex((c) => c.id === vm.selectedComment.id)
                    if (commentIndex !== -1) {
                        vm.comments.splice(commentIndex, 1)
                    }
                    vm.closeEditComment()
                    vm.selectedComment = {
                        content: "",
                    }
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        // Post a new comment.
        postComment() {
            if (this.currentComment == "") {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post(`/api/sessions/${this.currentSession.id}/nodes/${this.currentNodeId}/comments/`, {
                content: this.currentComment,
            })
                .then(() => {
                    // Fetching new comments after a successful post just in case another comment
                    // was made in the meantime.
                    vm.fetchComments()
                    vm.currentComment = ""
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        // Open the username modal.
        openUsernameModal() {
            // Make sure the comment modal is hidden.
            $("#comment-modal").modal("hide")
            this.$refs.anonymousUserForm.resetValidate()
            this.anonymousUser.username = this.currentSession.anonymous_username
            $("#session-name-modal").modal("show")
        },
        // Save the user's username.
        saveUsername() {
            if (!this.$refs.anonymousUserForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.put(`/api/sessions/${this.currentSession.id}/username/`, {
                username: this.anonymousUser.username,
            })
                .then((response) => {
                    vm.$store.commit("setSessionUsername", response.data.anonymous_username)
                    $("#session-name-modal").modal("hide")
                    // Refresh and open the comments modal.
                    vm.fetchComments()
                    vm.openComments()
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        // Open the notepad modal.
        openNotes() {
            $("#notepad-modal").modal("show")
        },
        // Save notepad notes.
        saveNotes() {
            this.notesSaving = true
            this.$store.commit("setSessionNotes", this.learnerNotes)
            this.saveNotesDebounced()
        },
        // Debounced notes saving, as this is triggered during user input to the notepad.
        saveNotesDebounced: debounce(async function () {
            await this.saveUpdatedNotes()
            this.notesSaving = false
        }, 300),
        saveUpdatedNotes() {
            http.post(`/api/students/sessions/${this.currentSession.id}/notes/`, {
                notes_content: this.learnerNotes,
            })
        },
        // Fetch learner resource tabs.
        fetchResourceTabs() {
            this.resourceModalLoading = true
            let vm = this
            http.get(`/api/students/sessions/${this.sessionID}/resources/`)
                .then(function (response) {
                    vm.learnerResourceTabs = response.data
                    if (vm.learnerResourceTabs.length) {
                        vm.selectedResourceTab = vm.learnerResourceTabs[0]
                    }
                })
                .finally(function () {
                    vm.resourceModalLoading = false
                })
        },
        // Open the learner resources modal.
        openResources() {
            this.hasOpenedResources = true
            $("#resources-modal").modal("show")
            let vm = this
            this.$nextTick(() => {
                let initialHeight = vm.$refs.resourceTabContainer.offsetHeight
                if (initialHeight > vm.minResourceTabHeight) {
                    vm.minResourceTabHeight = vm.$refs.resourceTabContainer.offsetHeight
                }
            })
        },
        // Select a learner resource tab.
        selectResourceTab(tab) {
            this.selectedResourceTab = tab
        },
        openResetSession() {
            $("#reset-session-modal").modal("show")
        },
        resetSession() {
            $("#reset-session-modal").modal("hide")
            this.$emit("reset-session")
        },
        openSupportModal() {
            // Open the main support modal, located in the TopNav component.
            $("#support-modal").modal("show")
        },
        // Send the user's support request.
        sendSupport() {
            // Check form validity.
            if (!this.$refs.supportForm.validate()) {
                return
            }
            let sessionCase = this.$store.getters.sessionCase
            let caseName = sessionCase ? `ID: ${sessionCase.id} - ${sessionCase.public_title}` : null
            this.modalLoading = true
            let vm = this
            http.post("/api/support/", {
                email: this.support.email,
                message: this.support.message,
                view: this.$route.path,
                caseName: caseName,
            })
                .then(function () {
                    $("#session-support-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Your support request has been submitted. Someone on our team will get back to you soon.",
                        data: {},
                    })
                    // Reset support message.
                    vm.support.message = ""
                })
                .finally(function () {
                    vm.modalLoading = false
                    vm.$refs.supportForm.resetValidate()
                })
        },
        // Starts the learner toolbar focussed tour, if there are any tools that need to
        // be highlighted.
        startToolbarTour() {
            // Don't show the learner tool on mobile screens for now. There are issues with tour step
            // positioning on small screens, which degrades the experience more than it helps. Revisit
            // this when a better solution for tours is found.
            let isSmallScreen = window.innerWidth < 576
            if (this.$store.getters.learnerToursSilenced || isSmallScreen) {
                return
            }
            let vm = this
            vm.$nextTick(() => {
                let translatedSteps = this.$store.getters.learnerTourStepTranslations
                // Add a tour step for each (important) toolbar item that's enabled/visible.
                if (vm.shareLinkNavigationEnabled) {
                    vm.toolbarTourSteps.push({
                        target: '[data-v-tour="navigation"]',
                        header: translatedSteps.navigation.header,
                        content: translatedSteps.navigation.content,
                        params: {
                            enableScrolling: false,
                            placement: "bottom-end",
                        },
                    })
                }
                if (vm.caseObj.learner_resources_enabled) {
                    vm.toolbarTourSteps.push({
                        target: '[data-v-tour="resources"]',
                        header: translatedSteps.resources.header,
                        content: translatedSteps.resources.content,
                        params: {
                            enableScrolling: false,
                            placement: "bottom-end",
                        },
                    })
                }
                if (vm.caseObj.global_notepad_enabled) {
                    vm.toolbarTourSteps.push({
                        target: '[data-v-tour="notes"]',
                        header: translatedSteps.notepad.header,
                        content: translatedSteps.notepad.content,
                        params: {
                            enableScrolling: false,
                            placement: "bottom-end",
                        },
                    })
                }
                if (vm.showCommentsBtn) {
                    vm.toolbarTourSteps.push({
                        target: '[data-v-tour="comments"]',
                        header: translatedSteps.comments.header,
                        content: translatedSteps.comments.content,
                        params: {
                            enableScrolling: false,
                            placement: "bottom-end",
                        },
                    })
                }
                if (vm.showLiveScore) {
                    // There are two scorebar component instances in this view that toggle in
                    // visibility based on screen size - highlight the right scorebar depending
                    // on the screen size.
                    let target = window.innerWidth > 1080 ? "scorebar" : "scorebar-sm"
                    vm.toolbarTourSteps.push({
                        target: `[data-v-tour="${target}"]`,
                        header: translatedSteps.score.header,
                        content: translatedSteps.score.content,
                        params: {
                            enableScrolling: false,
                        },
                    })
                }
                // Only proceed with starting the tour if there are tour steps to show.
                if (vm.toolbarTourSteps.length) {
                    vm.$tours["sessionToolbarTour"].start()
                }
            })
        },
        endToolbarTourCb() {
            this.hasViewedTour = true
        },
        silenceAllTours(tour) {
            // Stop the current tour.
            tour.stop()
            // Locally store the user's preference to silence all tours.
            this.$store.commit("setLearnerToursSilenced", true)
        },
        communityBrowseUrl(id) {
            return this.$router.resolve({ name: "CommunityCaseDetail", params: { id: id } }).href
        },
        // Used to fetch information about the original community scenario that content
        // was derived from.
        fetchOriginalCaseDetails(caseId) {
            if (!this.isCreativeCommonsDerivative) {
                return
            }
            return new Promise((resolve) => {
                http.get(`/api/community/cases/${caseId}/`).then(function (response) {
                    resolve(response.data)
                })
            })
        },
        // Fetch details about each case that the content derives from.
        async updateDerivativeInfo() {
            let vm = this
            const fetchPromises = this.derivedFromCaseIds.map(async (caseId) => {
                const details = await vm.fetchOriginalCaseDetails(caseId)
                return details
            })
            let derivedFromCases = await Promise.all(fetchPromises)
            this.derivedFromCases = derivedFromCases
        },
        // Trim a string to the desired length, for display.
        trimString(str, length) {
            return Utilities.trimString(str, length)
        },
        // Returns a list of contributors' names for a given case.
        contributorListDisplay(caseObj) {
            if (!caseObj.contributors) {
                return ""
            }
            return this.trimString(caseObj.contributors.map((c) => c.publisher.name).join(", "), 60)
        },
    },
    created() {
        this.fetchResourceTabs()
    },
    mounted() {
        let isPreviewingFirstNode = this.editMode && this.$store.getters.nodeOptions.length == 1
        let isFirstNode = this.currentNode.is_first_node
        // Only show the tour on the first node, and if the user hasn't
        // seen the tour yet. Don't show the tour if the user just started editing a
        // new case and is previewing the first node. Don't show the tour if the resume
        // session query parameter is set, because this means the user has already seen
        // the initial tour.
        if (isFirstNode && !this.hasViewedTour && !isPreviewingFirstNode && !this.$route.query.resume) {
            this.startToolbarTour()
        }
        this.updateDerivativeInfo()
        this.fetchComments()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.edit-switch {
    transform: scale(1.2);
}

.edit-switch-label {
    font-weight: 500;
    min-width: 110px;
}

.nav-link {
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    @media (min-width: $breakpoint-md) {
        padding-top: 10px;
        padding-bottom: 10px;
        padding-left: 0.7rem;
        padding-right: 0.7rem;
    }
}

.nav-item-sm-hidden {
    display: none;
    @media (min-width: $breakpoint-sm) {
        display: list-item;
    }
}

.navbar-nav-tools {
    padding-top: 1px;
    padding-bottom: 1px;
    margin-bottom: 8px;
    @media (min-width: $breakpoint-sm) {
        padding-left: 8px;
        padding-right: 8px;
    }
}

.learner-toolbar {
    margin-left: 10px;
}

.btn-transparent {
    padding: 3px !important;
}

li {
    list-style-type: none;
}

ul {
    padding-left: 0px;
    padding-right: 0px;
    margin-bottom: 0px;
}

.comments-badge {
    margin-left: 2px;
    font-size: 60%;
    padding-left: 4px;
    padding-right: 4px;
}

.btn-create {
    height: 30px;
    width: 30px;
}

.global-style-dropdown {
    width: 260px;
}

.community-detail-link {
    color: $primary;
}

.navbar-nav-tools {
    min-height: 48px;
}

.bg-toolbar {
    background-color: rgb(255 255 255 / 92%);
}

.cc-derivation {
    max-width: 410px;
    line-height: 1.2;
}

.cc-derivation-container {
    @media (min-width: $breakpoint-sm) {
        height: 0px;
    }

    .cc-derivation-item {
        margin-top: 6px;
    }
}

.tour-btn-silence {
    font-size: 13px;
}

.tour-header {
    font-size: 1.2rem;
    font-weight: 500;
}

.tour-content {
    margin-bottom: 20px;
}

.unread-marker {
    margin-left: 3px;
    margin-right: 3px;
}

.page-logo-circle {
    width: 40px;
    height: 40px;
}
</style>